import Question from './Question';
import User from 'records/User';
import moment from 'moment';
import toPairs from 'lodash/toPairs';
import sum from 'utils/sum';
import isNil from 'lodash/isNil';
import {Record, List} from 'immutable';
import Answer from 'records/Answer';

export const required = ['name'];

export const scoreRange = [0, 5];

export const NA = 'n/a';

// All of these schemes will map to 1-5 behind the scenes;
// for OneToFive: 1=1, 2=2, 3=3, 4=4, 5=5
// for OneToThree: 1=1, 2=3, 3=5
// for YesNo: no=1, yes=5
// for PassFail: no=1, yes=5

// XXX we support snaked only because some apis still go to python. Snaked should be buried at the serialization boundary
// and pascal should be the standard moving forward. Once we're off python for scorecard, snaked should go away.
export const scorecardTypes = {
  YesNo: {
    label: 'Yes / No',
    snaked: 'yes_no',
    answers: [
      {label: NA, value: null, header: '', tooltip: 'N/A'},
      {label: 'no', value: 0, header: '', tooltip: 'No'},
      {label: 'yes', value: 5, header: '', tooltip: 'Yes'},
    ],
  },
  PassFail: {
    label: 'Pass / Fail',
    snaked: 'pass_fail',
    answers: [
      {label: NA, value: null, header: '', tooltip: 'N/A'},
      {label: 'fail', value: 0, header: '', tooltip: 'Fail'},
      {label: 'pass', value: 5, header: '', tooltip: 'Pass'},
    ],
  },
  OneToThree: {
    label: 'One to Three',
    snaked: 'one_to_three',
    answers: [
      {label: NA, value: null, header: '', tooltip: 'N/A'},
      {label: '0', value: 0, header: '', tooltip: ''},
      {label: '1', value: 1, header: 'No', tooltip: 'No'},
      {label: '2', value: 3, header: 'Maybe', tooltip: 'Maybe'},
      {label: '3', value: 5, header: 'Yes', tooltip: 'Yes'},
    ],
  },
  OneToFive: {
    label: 'One to Five',
    snaked: 'one_to_five',
    answers: [
      {label: NA, value: null, header: '', tooltip: 'N/A'},
      {label: '0', value: 0, header: '', tooltip: ''},
      {label: '1', value: 1, header: 'No', tooltip: 'No'},
      {label: '2', value: 2, header: '', tooltip: 'No - Maybe'},
      {label: '3', value: 3, header: 'Maybe', tooltip: 'Maybe'},
      {label: '4', value: 4, header: '', tooltip: 'Maybe - Yes'},
      {label: '5', value: 5, header: 'Yes', tooltip: 'Yes'},
    ],
  },
  OneTwo: {
    label: 'One / Two',
    snaked: 'one_two',
    answers: [
      {label: NA, value: null, header: '', tooltip: 'N/A'},
      {label: '0', value: 0, header: '', tooltip: ''},
      {label: '1', value: 3, header: '', tooltip: 'One'},
      {label: '2', value: 5, header: '', tooltip: 'Two'},
    ],
  },
  ContentDetail: {
    label: 'Content Detail',
    snaked: 'content_detail',
    answers: [
      {label: '1', value: 1, header: 'No', tooltip: 'No'},
      {label: '2', value: 2, header: '', tooltip: 'No - Maybe'},
      {label: '3', value: 3, header: 'Maybe', tooltip: 'Maybe'},
      {label: '4', value: 4, header: '', tooltip: 'Maybe - Yes'},
      {label: '5', value: 5, header: 'Yes', tooltip: 'Yes'},
    ],
  },
};

// TODO remove the need for this stupid key property and maybe some of these other properties here that are super similar
function generateKeyProperty(type) {
  type.answers.forEach(answer => (answer.key = answer.value === null ? NA : answer.value + ''));
}
Object.values(scorecardTypes).map(generateKeyProperty);

/**
 * Get either the default header text for the specified scoring scheme/point,
 * Or the label scheme's header if it is specified
 *
 * @param scoringScheme
 * @param point
 * @param labelScheme
 * @return label or default for the scoring scheme/point value
 */
export function labelOrDefault(scoringScheme, point, labelScheme) {
  const defaultForPoint = scorecardTypes[scoringScheme].answers.find(a => a.key === point);
  const customLabel =
    labelScheme && labelScheme.labels && nullToNa(labelScheme.labels).find(l => l.point === point)
      ? nullToNa(labelScheme.labels).find(l => l.point === point).label
      : undefined;
  return !isNil(customLabel) ? customLabel : defaultForPoint && defaultForPoint.header;
}

/**
 * Get either the default header text for the specified scoring scheme/point,
 * Or the label scheme's header if it is specified
 *
 * @param scoringScheme
 * @param point
 * @param labelScheme
 * @return label or default for the scoring scheme/point value
 */
export function labelOrDefaultTooltip(scoringScheme, point, labelScheme) {
  const defaultForPoint = scorecardTypes[scoringScheme].answers.find(a => a.key === point);
  const customLabel =
    labelScheme && labelScheme.labels && nullToNa(labelScheme.labels).find(l => l.point === point)
      ? nullToNa(labelScheme.labels).find(l => l.point === point).label
      : undefined;
  return customLabel ? customLabel : defaultForPoint.tooltip;
}

export const nullToNa = labels => {
  return labels.map(l => (l.point === null ? {...l, point: NA} : l));
};

export const naToNull = labels => {
  return labels.map(l => (l.point === NA ? {...l, point: null} : l));
};

export const pascalToSnakeScoringScheme = scheme => scorecardTypes[scheme].snaked;

export const snakeToCamelScoringScheme = snakedScheme =>
  toPairs(scorecardTypes).find(([, type]) => type.snaked === snakedScheme)[0];

export const answersFor = scheme => {
  const pascalType = scorecardTypes[scheme]?.answers;
  if (pascalType) return pascalType;

  const snakeType = Object.values(scorecardTypes).find(t => t.snaked === scheme);
  if (snakeType) return snakeType;

  console.error("Couldn't match type for:", scheme);
  return [];
};

export default class Scorecard extends Record({
  id: null,
  active: true,
  accountId: null,
  departmentId: null,
  name: '',
  notes: true,
  questions: List(),
  results: List(),
  updated: null,
  scoringScheme: 'OneToFive',
  activityTypes: List.of('CALL'),
  entityTypes: List(),
}) {
  constructor(object = {}) {
    super(
      Object.assign({}, object, {
        updated: object.updated && Date.parse(object.updated),
        questions: List((object.questions || []).map(question => new Question(question))),
        results: List(object.results || []).map(result => new ScorecardResult(result)),
        scoredBy: new User(object.scoredBy),
        scoredOn: object.scoredOn ? moment(object.scoredOn) : null,
        scoringScheme: object.scoringScheme && snakeToCamelScoringScheme(object.scoringScheme),
      })
    );
  }
  hasBeenReviewedBy(userId) {
    return this.results.map(card => card.reviewerUserId).includes(userId);
  }
  getResultsAverageScore() {
    const {results} = this;
    const scores = results.map(result => result.score).filter(score => score !== null);
    if (scores.size === 0) {
      return null;
    }
    return sum(scores) / scores.count();
  }
  getQuestionSkillIds() {
    return this.questions.flatMap(q => q.skillIds).toSet();
  }
}

export class ScorecardResult extends Record({
  accountId: undefined,
  activity: undefined,
  answers: undefined,
  cardId: undefined,
  contact: undefined,
  deleted: undefined,
  draft: undefined,
  entityId: undefined,
  entityType: undefined,
  id: undefined,
  notes: undefined,
  outcome: undefined,
  reviewId: undefined,
  reviewedUser: undefined,
  reviewedUserId: undefined,
  reviewerUser: undefined,
  reviewerUserId: undefined,
  score: undefined,
  scorecard: undefined,
  scoredDate: undefined,
  total: undefined,
  value: undefined,

  // TODO below are deprecated
  date: undefined,
  created: undefined,
  updated: undefined,
  userAvatar: undefined,
  lastName: undefined,
  firstName: undefined,
  displayName: undefined,
  color: undefined,
}) {
  constructor(object = {}) {
    super({
      ...object,
      scoredDate: object.scoredDate ? moment(object.scoredDate) : undefined,
      answers: List((object.answers || []).map(answer => new Answer(answer))),
      reviewedUser: object.reviewedUser ? new User(object.reviewedUser) : undefined,
      reviewerUser: object.reviewerUser ? new User(object.reviewerUser) : undefined,
      contact: object.contact ? new User(object.contact) : undefined,
      scorecard: object.scorecard ? new Scorecard(object.scorecard) : undefined,
    });
  }
}
